import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  ListGroup,
  Button,
  Collapse,
} from "react-bootstrap";
import { toast } from "react-toastify";
import PastTermsTable from "../PastTermsTable";
import AcademicYearSelector from "../AcademicYearSelector";
import { useAcademicYear } from "../AcademicYearContext";
import api from "common/api";
import "../Dashboard.css";
import { Term } from "common/types";

const calculateMonthlyHours = (terms: Term[]) => {
  const monthlyHours: { [key: string]: number } = {};

  terms.forEach((term) => {
    const startDate = new Date(term.start);
    const endDate = new Date(term.end);

    const durationHours =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

    const month = startDate.toLocaleString("hr", {
      year: "numeric",
      month: "long",
    });

    if (!monthlyHours[month]) {
      monthlyHours[month] = 0;
    }

    monthlyHours[month] += durationHours;
  });

  return monthlyHours;
};

const TeacherDashboardLessons = () => {
  const [reservedTerms, setReservedTerms] = useState<Term[]>([]);
  const [pastTerms, setPastTerms] = useState<Term[]>([]);
  const [monthlyHours, setMonthlyHours] = useState<{ [key: string]: number }>(
    {},
  );
  const [expandedDetails, setExpandedDetails] = useState<{
    [key: number]: boolean;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const { selectedAcademicYear, getAcademicYear, getCurrentAcademicYear } =
    useAcademicYear();

  useEffect(() => {
    fetchReservedTerms();
    fetchAllPaidTerms();
  }, []);

  const fetchReservedTerms = async () => {
    setLoading(true);
    try {
      const now = new Date();
      now.setHours(now.getHours() - 2);
      const twoHoursAgo = now.toISOString();

      const response = await api.get(
        `/calendars/?status=PAID&start_after=${encodeURIComponent(
          twoHoursAgo,
        )}`,
      );
      setReservedTerms(response.data);

      const allPaidTermsResponse = await api.get(`/calendars/?status=PAID`);
      setMonthlyHours(calculateMonthlyHours(allPaidTermsResponse.data));
    } catch (error) {
      console.error(error);
      toast.error("Greška prilikom dohvaćanja termina.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPaidTerms = async () => {
    try {
      const response = await api.get(`/calendars/?status=PAID`);
      setPastTerms(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleDetails = (index: number) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Filter terms by academic year
  const filterTermsByAcademicYear = (terms: Term[]): Term[] => {
    if (selectedAcademicYear === "all") {
      return terms;
    }

    const targetYear =
      selectedAcademicYear === "current"
        ? getCurrentAcademicYear()
        : selectedAcademicYear;

    return terms.filter((term) => {
      const termAcademicYear = getAcademicYear(new Date(term.start));
      return termAcademicYear === targetYear;
    });
  };

  const filteredPastTerms = filterTermsByAcademicYear(pastTerms);
  const filteredMonthlyHours = calculateMonthlyHours(filteredPastTerms);

  return (
    <Container>
      <AcademicYearSelector terms={pastTerms} />
      <Row>
        <Col>
          <h2>Rezervirani termini</h2>
          {loading ? (
            <p>Učitavanje...</p>
          ) : (
            <ListGroup>
              {reservedTerms.length > 0 ? (
                reservedTerms.map((term, index) => {
                  const subjectName = term.subject?.name || "Predmet";
                  return (
                    <ListGroup.Item key={term.id}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{subjectName}</strong> -{" "}
                          {new Date(term.start).toLocaleString("hr")} do{" "}
                          {new Date(term.end).toLocaleString("hr")}
                        </div>
                        <Button
                          variant="link"
                          onClick={() => toggleDetails(index)}
                          aria-controls={`term-details-${index}`}
                          aria-expanded={!!expandedDetails[index]}
                        >
                          {expandedDetails[index]
                            ? "Sakrij detalje"
                            : "Prikaži detalje"}
                        </Button>
                      </div>

                      <Collapse in={!!expandedDetails[index]}>
                        <div id={`term-details-${index}`}>
                          {term.meet_url && (
                            <div className="mt-2 teacher-dashboard-lessons__video-link">
                              <strong>Video link: </strong>
                              <a href={term.meet_url}>{term.meet_url}</a>
                            </div>
                          )}

                          {term.remark && (
                            <div className="mt-2">
                              <strong>Napomena: </strong>
                              {term.remark}
                            </div>
                          )}

                          {term.student && (
                            <>
                              <div className="mt-2">
                                <strong>Učenik: </strong>
                                {term.student?.first_name}{" "}
                                {term.student?.last_name}
                              </div>

                              <div className="mt-2">
                                <strong>Kontakt email: </strong>
                                <a href={`mailto:${term.student?.email}`}>
                                  {term.student?.email}
                                </a>
                              </div>

                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="mt-2"
                                href={`mailto:${term.student?.email}`}
                                as="a"
                              >
                                Pošalji email učeniku
                              </Button>
                            </>
                          )}
                        </div>
                      </Collapse>
                    </ListGroup.Item>
                  );
                })
              ) : (
                <ListGroup.Item>
                  Nema rezerviranih termina poduka
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <h2>Statistika po mjesecu</h2>
          <ListGroup>
            {Object.keys(filteredMonthlyHours).length > 0 ? (
              Object.entries(filteredMonthlyHours).map(
                ([month, hours], index) => (
                  <ListGroup.Item key={index}>
                    <strong>{month}</strong>: {hours.toFixed(1)} sati
                  </ListGroup.Item>
                ),
              )
            ) : (
              <ListGroup.Item>Nema statistike</ListGroup.Item>
            )}
          </ListGroup>
        </Col>
      </Row>
      <PastTermsTable terms={filteredPastTerms} />
    </Container>
  );
};

export default TeacherDashboardLessons;
